import { SxProps } from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";

export type ChipTone = "success" | "warning" | "error" | "info" | "neutral";

/**
 * Raw CSS for a soft "status chip": a translucent tint of the tone colour, matching light text,
 * and a subtle border — calm on the dark navy theme instead of a solid, garish filled chip.
 * `neutral` is a plain muted outline. Shared by both `softChipSx` (explicit per-chip use) and the
 * global MuiChip theme override (so every filled semantic chip gets this look automatically).
 */
export function softChipStyle(theme: Theme, tone: ChipTone) {
  if (tone === "neutral") {
    return {
      fontWeight: 500,
      backgroundColor: "transparent",
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
    };
  }
  const c = theme.palette[tone];
  return {
    fontWeight: 600,
    backgroundColor: alpha(c.main, 0.16),
    color: c.light,
    border: `1px solid ${alpha(c.main, 0.45)}`,
  };
}

/** sx wrapper: `<Chip size="small" label="…" sx={softChipSx("warning")} />`. */
export function softChipSx(tone: ChipTone): SxProps<Theme> {
  return (theme: Theme) => softChipStyle(theme, tone);
}
